/**
 * Orgus Watch in the console (ADR 0058 decision 3): the incident rows in Overview's Needs you and
 * the top bar notices, read from state.ext.watch. Pure, so the ranking, the row's one status, its one
 * action and the notices are tested without a page.
 */
import { INCIDENT_DETECTOR_LABEL, INCIDENT_SEVERITY_LABEL, INCIDENT_STATE_LABEL, attentionOf, incidentNeedsPerson, watchStale } from '@orgus/protocol';
import type { Incident, IncidentSeverity, OrphanTier, WatchState } from '@orgus/protocol';
import { ORPHAN_TIERS } from '@orgus/protocol';
import { formatClock, formatDate, formatDuration, plural } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import type { Notice, NoticeTone } from '../../components/shell/notices.ts';

/** The slice, or null when the hub sent none (the module is not installed, or an older hub). */
export function watchStateOf(ext: Record<string, unknown> | null | undefined): WatchState | null {
  const w = ext?.watch as Partial<WatchState> | null | undefined;
  if (!w || typeof w !== 'object' || !Array.isArray(w.incidents)) return null;
  return { incidents: w.incidents, lastTickAt: w.lastTickAt ?? null, tickMs: w.tickMs ?? 30_000, detectors: w.detectors ?? [] };
}

/**
 * The row's one status: an open incident shows its severity, or that Orgus handles it (ORGU-92);
 * a taken one shows who has it.
 */
export function incidentRowStatus(i: Pick<Incident, 'state' | 'severity'> & Partial<Pick<Incident, 'handling'>>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  if (i.state === 'open' && i.handling?.state === 'working') return { tone: 'working', icon: 'working', label: 'Handled by Orgus' };
  if (i.state === 'open' && i.handling?.state === 'info') return { tone: 'neutral', icon: 'queued', label: 'For your information' };
  if (i.state === 'fixing') return { tone: 'working', icon: 'working', label: INCIDENT_STATE_LABEL.fixing };
  if (i.state === 'acknowledged') return { tone: 'neutral', icon: 'queued', label: INCIDENT_STATE_LABEL.acknowledged };
  if (i.state === 'resolved') return { tone: 'neutral', icon: 'done', label: INCIDENT_STATE_LABEL.resolved };
  if (i.state === 'dismissed') return { tone: 'neutral', icon: 'stopped', label: INCIDENT_STATE_LABEL.dismissed };
  return SEVERITY_STATUS[i.severity];
}

const SEVERITY_STATUS: Record<IncidentSeverity, Pick<RowStatusProps, 'tone' | 'icon' | 'label'>> = {
  critical: { tone: 'danger', icon: 'failed', label: INCIDENT_SEVERITY_LABEL.critical },
  high: { tone: 'attention', icon: 'warning', label: INCIDENT_SEVERITY_LABEL.high },
  medium: { tone: 'attention', icon: 'warning', label: INCIDENT_SEVERITY_LABEL.medium },
  low: { tone: 'neutral', icon: 'warning', label: INCIDENT_SEVERITY_LABEL.low },
};

/** "you" for the signed-in person (and the demo's owner), else the id as the hub wrote it. */
export function whoLabel(by: string | undefined, me: string | null): string | null {
  if (!by) return null;
  return by === me || by === 'owner' ? 'you' : by;
}

/** How long an incident has been open, in its largest unit: "45s", "14m", "2h", "3d". */
export function ageText(ms: number): string {
  return ms < 1_000 ? '0s' : formatDuration(ms).split(' ')[0]!;
}

/** The two meta facts: who has it (or who is responsible) and how long it has been open. */
export function incidentMeta(i: Incident, now: number, me: string | null): { who: string; age: string } {
  const by = i.state === 'open' ? null : whoLabel(i.by, me);
  return { who: by ? `by ${by}` : i.responsible.label, age: ageText(now - i.openedAt) };
}

/** What the row's one button does. */
export type PrimaryAction =
  | { kind: 'act'; label: string }
  | { kind: 'open'; label: string; to: string }
  | { kind: 'copy'; label: string; text: string }
  | { kind: 'ack'; label: string };

/**
 * An open incident's button is its own action; a taken one has none in the row (its action
 * stays in the menu). 'hand-back', 'stop-server' and 'hub' run on the hub (a hub action's shell
 * command stays behind a disclosure, machineCommandOf); 'open' goes to the console path; 'command'
 * copies the command; 'ack' (and an action without the target it needs) acknowledges.
 */
export function primaryActionOf(i: Pick<Incident, 'state' | 'action'>): PrimaryAction | null {
  if (i.state !== 'open') return null;
  return actionOf(i.action);
}

export function actionOf(a: Incident['action']): PrimaryAction {
  if (a.kind === 'hand-back' || a.kind === 'stop-server' || a.kind === 'hub') return { kind: 'act', label: a.label };
  if (a.kind === 'open' && a.target) return { kind: 'open', label: a.label, to: a.target };
  if (a.kind === 'command' && a.target) return { kind: 'copy', label: a.label, text: a.target };
  return { kind: 'ack', label: a.kind === 'ack' && a.label ? a.label : 'Acknowledge' };
}

/** ORGU-186: a hub action's shell equivalent, for a person at the machine; null otherwise. */
export function machineCommandOf(a: Incident['action']): string | null {
  return a.kind === 'hub' && a.command ? a.command : null;
}

/** "Raised 3 times", or null for the first time. */
export function raisedText(count: number): string | null {
  return count > 1 ? `Raised ${plural(count, 'time')}` : null;
}

/** "Last checked 12s ago", "Last checked just now", or "Not checked yet" before the first tick. */
export function lastCheckedText(lastTickAt: number | null, now: number): string {
  if (lastTickAt == null) return 'Not checked yet';
  const ago = now - lastTickAt;
  return ago < 1_000 ? 'Last checked just now' : `Last checked ${formatDuration(ago)} ago`;
}

/** "14:02" today, "Oct 17, 14:02" before. */
function whenText(ts: number, now: number): string {
  return new Date(ts).toDateString() === new Date(now).toDateString() ? formatClock(ts) : `${formatDate(ts, now)}, ${formatClock(ts)}`;
}

/** The stale watch's words: the time it last checked, why it matters, what to do. */
export function staleText(lastTickAt: number, now: number): { title: string; body: string } {
  return {
    title: `The watch has not checked since ${whenText(lastTickAt, now)}`,
    body: 'Incidents may be out of date and new problems go unseen. Restart the hub with orgus up, then reload this page.',
  };
}

/** The stale watch in a few words, for a header caption: "Not checked since 14:02". */
export function staleCaption(lastTickAt: number, now: number): string {
  return `Not checked since ${whenText(lastTickAt, now)}`;
}

/** The active incidents, severity first, then the oldest. */
export function attentionRows(state: WatchState | null): Incident[] {
  return state ? attentionOf(state.incidents) : [];
}

/**
 * Active incidents split by who has them (ORGU-92): `person` counts in Needs you (Orgus gave up,
 * the owner chose People, or nothing handles that kind); `orgus` is what Orgus handles or only
 * informs about, shown apart and never counted.
 */
export function splitHandled(incidents: readonly Incident[]): { person: Incident[]; orgus: Incident[] } {
  const person: Incident[] = [];
  const orgus: Incident[] = [];
  for (const i of incidents) (incidentNeedsPerson(i) ? person : orgus).push(i);
  return { person, orgus };
}

/** The console path of the mission handling an incident, or null. */
export function handlingPath(i: Pick<Incident, 'handling'>): string | null {
  return i.handling?.missionId ? `/missions/${encodeURIComponent(i.handling.missionId)}` : null;
}

/** ORGU-299: The console path of the fix session handling an incident, or null. */
export function fixSessionPath(i: Pick<Incident, 'fixSession' | 'handling'>): string | null {
  const sid = i.fixSession ?? i.handling?.sessionId;
  return sid ? `/sessions?session=${encodeURIComponent(sid)}` : null;
}

/** ORGU-299: The console path of the revert handling an incident, or null. */
export function revertPath(i: Pick<Incident, 'revertSha' | 'handling'>): string | null {
  const sha = i.revertSha ?? i.handling?.revertSha;
  return sha ? `/pipelines/landing?sha=${encodeURIComponent(sha)}` : null;
}

/** The detail facts an expanded row shows, after the incident's own evidence. */
export function detailFacts(i: Incident, me: string | null): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = i.evidence.map((f) => ({ label: f.label, value: String(f.value) }));
  out.push({ label: 'Detector', value: INCIDENT_DETECTOR_LABEL[i.detector] ?? i.detector });
  // An open row's status is its severity already; a taken one's is who has it.
  if (i.state !== 'open') out.push({ label: 'Severity', value: INCIDENT_SEVERITY_LABEL[i.severity] });
  out.push({ label: 'Responsible', value: i.responsible.label });
  if (i.handling && i.handling.state !== 'info') out.push({ label: i.handling.state === 'gave-up' ? 'Orgus gave up' : i.handling.state === 'people' ? 'Handled by' : 'Orgus', value: i.handling.state === 'people' ? 'People' : i.handling.text });
  const by = whoLabel(i.by, me);
  if (by && i.state !== 'open') out.push({ label: `${INCIDENT_STATE_LABEL[i.state]} by`, value: by });
  if (i.note) out.push({ label: 'Note', value: i.note });
  if (i.sha) out.push({ label: 'Fix', value: i.sha.slice(0, 10) });
  const revertSha = i.revertSha ?? i.handling?.revertSha;
  if (revertSha) out.push({ label: 'Revert', value: revertSha.slice(0, 10) });
  const fixSession = i.fixSession ?? i.handling?.sessionId;
  if (fixSession) out.push({ label: 'Fix session', value: fixSession.slice(0, 8) });
  return out;
}

const SEVERITY_TONE: Record<IncidentSeverity, NoticeTone> = { critical: 'danger', high: 'attention', medium: 'attention', low: 'neutral' };

/** The top bar notice for a watch that stopped checking, or null. */
export function staleNotice(state: WatchState | null, now: number): Notice | null {
  if (!state || state.lastTickAt == null || !watchStale(state, now)) return null;
  const t = staleText(state.lastTickAt, now);
  return { id: 'watch.stale', tone: 'attention', kind: 'watch', title: t.title, body: t.body, order: -1 };
}

/** One top bar notice per incident nobody has taken yet, so the notice count includes the open incident count. */
export function incidentNotices(state: WatchState | null): Notice[] {
  return attentionRows(state)
    .filter((i) => i.state === 'open' && incidentNeedsPerson(i))
    .map((i, n): Notice => {
      const a = actionOf(i.action);
      return {
        id: `watch.${i.id}`,
        tone: SEVERITY_TONE[i.severity],
        kind: 'incident',
        title: i.title,
        body: i.why,
        order: n,
        since: i.openedAt,
        actions: [a.kind === 'open' ? { id: 'open', label: a.label, to: a.to, emphasis: 'secondary' } : { id: 'view', label: 'View incident', to: incidentPath(i.id), emphasis: 'secondary' }],
      };
    });
}

/** Every notice the watch publishes: the stale watch first, then the open incidents. */
export function watchNotices(state: WatchState | null, now: number): Notice[] {
  const stale = staleNotice(state, now);
  return [...(stale ? [stale] : []), ...incidentNotices(state)];
}

/** Overview with the incident's row open. */
export function incidentPath(id: string): string {
  return `/?incident=${encodeURIComponent(id)}`;
}

/** How many rows show before "and N more". */
export const ATTENTION_ROWS = 5;

/** "1 incident", "3 incidents". */
export function incidentsLabel(n: number): string {
  return plural(n, 'incident');
}

/** How many active incidents of each severity, worst first, leaving out the empty ones: "2 critical, 5 high". */
export function severitySummary(rows: readonly Pick<Incident, 'severity'>[]): Array<{ severity: IncidentSeverity; count: number }> {
  const order: IncidentSeverity[] = ['critical', 'high', 'medium', 'low'];
  return order.map((severity) => ({ severity, count: rows.filter((r) => r.severity === severity).length })).filter((x) => x.count > 0);
}

const TIER_LABEL: Record<OrphanTier, { label: string; hint: string }> = {
  merged: { label: 'Merged', hint: 'Every commit is on main: Orgus removes it, no mission' },
  salvage: { label: 'Salvage', hint: 'Merged with uncommitted leftovers: kept under refs/salvage, then removed' },
  submit: { label: 'Land', hint: 'Unique commits that merge clean and pass the fast checks: submitted without a mission' },
  mission: { label: 'Mission', hint: 'Needs a land-or-drop mission: each row says why' },
};

/** ORGU-138: orphaned work by triage tier, cheapest first, leaving out the empty ones. */
export function orphanTiers(rows: readonly Pick<Incident, 'handling'>[]): Array<{ tier: OrphanTier; count: number; label: string; hint: string }> {
  return ORPHAN_TIERS.map((tier) => ({ tier, count: rows.filter((r) => r.handling?.tier === tier).length, ...TIER_LABEL[tier] })).filter((x) => x.count > 0);
}
